import { useState, useEffect, useCallback } from "react";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import type { IPageInfo } from "@/interface/common.interface";
import type { IBranchWiseSalaryHead } from "@/interface/salaryHead.interface";
import type { ISalaryUserBackup } from "@/components/common/SalaryConfigTable";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import { createInitialPageInfo } from "./createSalary.constants";
import { getPageInfoUpdate, resetPage } from "./createSalary.utils";
import type { UseMasterDataReturn } from "./useMasterData";

export const useSalaryConfigTab = (masterData: UseMasterDataReturn) => {
  const [branchesWiseSalaryHead, setBranchesWiseSalaryHead] = useState<
    IBranchWiseSalaryHead[]
  >([]);
  const [salaryConfigData, setSalaryConfigData] = useState<ISalaryUserBackup[]>(
    [],
  );
  const [isLoadingSalaryConfig, setIsLoadingSalaryConfig] =
    useState<boolean>(false);
  const [salaryConfigPaginationData, setSalaryConfigPaginationData] =
    useState<IPageInfo>(createInitialPageInfo);

  // Fetch branch-wise salary heads (needed for column headers)
  const fetchBranchWiseSalaryHead = useCallback(async (branchId: string) => {
    if (!branchId) {
      setBranchesWiseSalaryHead([]);
      return;
    }
    try {
      const PATH = `branchId=${branchId}&pageSize=100`;
      const response = await SalaryHeadServices.getBranchWiseSalaryHead(PATH);
      if (response?.status || response?.statuscode === 200) {
        const list = response?.data?.branchSalaryHead || [];
        setBranchesWiseSalaryHead(list);
      } else {
        setBranchesWiseSalaryHead([]);
      }
    } catch (error) {
      ThrowError(error);
    }
  }, []);

  // Fetch salary heads when branch changes
  useEffect(() => {
    if (masterData.selectedBranchId) {
      fetchBranchWiseSalaryHead(masterData.selectedBranchId);
    }
  }, [masterData.selectedBranchId, fetchBranchWiseSalaryHead]);

  // Fetch branch-wise user salary (salary config data)
  const fetchBranchWiseUserSalary = useCallback(
    async (
      currentPage = salaryConfigPaginationData.currentPage,
      currentLimit = salaryConfigPaginationData.pageSize,
    ) => {
      if (!masterData.selectedBranchId && !masterData.selectedSiteId) return;

      setIsLoadingSalaryConfig(true);
      try {
        const queryParams = new URLSearchParams();
        if (masterData.selectedBranchId)
          queryParams.append("branchId", masterData.selectedBranchId);
        if (masterData.selectedSiteId)
          queryParams.append("siteId", masterData.selectedSiteId);
        if (masterData.selectedCategory)
          queryParams.append("category", masterData.selectedCategory);
        if (masterData.searchValue)
          queryParams.append("search", masterData.searchValue);
        queryParams.append("pageNumber", String(currentPage));
        queryParams.append("pageSize", String(currentLimit));

        const response = await SalaryHeadServices.getBranchWiseUserSalary(
          queryParams.toString(),
        );

        if (response?.status || response?.statuscode === 200) {
          const usersList: ISalaryUserBackup[] =
            response?.data?.users || response?.data?.user || [];
          setSalaryConfigData(usersList);
          setSalaryConfigPaginationData((prev) => ({
            ...prev,
            ...getPageInfoUpdate(
              response?.data?.pageInfo,
              usersList.length,
              currentLimit,
            ),
          }));
        } else {
          setSalaryConfigData([]);
          setSalaryConfigPaginationData((prev) => ({
            ...prev,
            total: 0,
            totalPages: 0,
          }));
        }
      } catch (error) {
        ThrowError(error);
        setSalaryConfigData([]);
      } finally {
        setIsLoadingSalaryConfig(false);
      }
    },
    [
      masterData.selectedBranchId,
      masterData.selectedSiteId,
      masterData.selectedCategory,
      masterData.searchValue,
      salaryConfigPaginationData.currentPage,
      salaryConfigPaginationData.pageSize,
    ],
  );

  // Save single user salary head update
  const handleSaveUserSalary = useCallback(
    async (
      userId: string,
      userSalaryPayload: { branchSalaryHeadId: string; amount: number }[],
    ) => {
      const payload = {
        userSalary: userSalaryPayload,
      };
      try {
        const response = await SalaryHeadServices.updateUserSalary(
          payload,
          userId,
        );
        if (response?.status || response?.statuscode === 200) {
          showToastNotification("success", "User salary updated successfully!");
          fetchBranchWiseUserSalary(
            salaryConfigPaginationData.currentPage,
            salaryConfigPaginationData.pageSize,
          );
        } else {
          showToastNotification(
            "danger",
            response?.message || "Failed to update user salary",
          );
        }
      } catch (error) {
        ThrowError(error);
      }
    },
    [
      fetchBranchWiseUserSalary,
      salaryConfigPaginationData.currentPage,
      salaryConfigPaginationData.pageSize,
    ],
  );

  // Fetch when employee tab becomes active (salary-break is a sub-tab of employee)
  useEffect(() => {
    if (
      masterData.activeTab === "employee" ||
      masterData.activeTab === "salary-config"
    )
      fetchBranchWiseUserSalary();
  }, [masterData.activeTab, fetchBranchWiseUserSalary]);

  const handleSalaryConfigChangePageSize = useCallback((newSize: number) => {
    setSalaryConfigPaginationData((prev) => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1,
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setSalaryConfigPaginationData(resetPage);
  }, []);

  return {
    branchesWiseSalaryHead,
    salaryConfigData,
    isLoadingSalaryConfig,
    salaryConfigPaginationData,
    setSalaryConfigPaginationData,
    handleSaveUserSalary,
    handleSalaryConfigChangePageSize,
    resetPagination,
    refetchBranchWiseUserSalary: fetchBranchWiseUserSalary,
  };
};

export type UseSalaryConfigTabReturn = ReturnType<typeof useSalaryConfigTab>;
